/**
 * Shapes → flattened polylines with length metrics. Canvas 2D has no path
 * metrics, so everything is flattened once per figure; that gives us
 * `revealAlong` (prefix by length) and `flowAlong` (point at distance) for
 * free, and the drawing quality is indistinguishable at figure sizes.
 */
import type { Geometry, Shape } from "./model";

export type Pt = [number, number];
export interface Subpath { pts: Pt[]; closed: boolean }
export interface Flat { subpaths: Subpath[]; length: number; cum: number[][] }

const CURVE_STEPS = 18;
const CIRCLE_STEPS = 56;

export function flatten(shape: Shape, g: Geometry): Flat {
  let subpaths: Subpath[];
  switch (shape) {
    case "rect": subpaths = [rect(g)]; break;
    case "circle": subpaths = [{ pts: arcPts(g.cx!, g.cy!, g.r!, 0, 360, CIRCLE_STEPS), closed: true }]; break;
    case "arc": subpaths = [{ pts: arcPts(g.cx!, g.cy!, g.r!, g.start!, g.sweep!, Math.max(8, Math.round(CIRCLE_STEPS * Math.abs(g.sweep!) / 360))), closed: false }]; break;
    case "line": subpaths = [{ pts: [[g.x1!, g.y1!], [g.x2!, g.y2!]], closed: false }]; break;
    case "polygon": subpaths = [{ pts: (g.points ?? []).map((p) => [p[0], p[1]] as Pt), closed: g.closed !== false }]; break;
    case "path": subpaths = parsePath(g.d ?? ""); break;
  }
  const cum: number[][] = [];
  let length = 0;
  for (const sp of subpaths) {
    const c: number[] = [0];
    const pts = sp.closed && sp.pts.length > 1 ? [...sp.pts, sp.pts[0]!] : sp.pts;
    for (let i = 1; i < pts.length; i++) c.push(c[i - 1]! + dist(pts[i - 1]!, pts[i]!));
    cum.push(c);
    length += c[c.length - 1] ?? 0;
  }
  return { subpaths, length, cum };
}

function rect(g: Geometry): Subpath {
  const { x = 0, y = 0, w = 0, h = 0, rx = 0 } = g;
  if (!rx) return { pts: [[x, y], [x + w, y], [x + w, y + h], [x, y + h]], closed: true };
  const r = Math.min(rx, w / 2, h / 2);
  const pts: Pt[] = [
    ...arcPts(x + w - r, y + r, r, -90, 90, 8),
    ...arcPts(x + w - r, y + h - r, r, 0, 90, 8),
    ...arcPts(x + r, y + h - r, r, 90, 90, 8),
    ...arcPts(x + r, y + r, r, 180, 90, 8),
  ];
  return { pts, closed: true };
}

/** Degrees clockwise from +x in screen coordinates, like Dart's addArc. */
function arcPts(cx: number, cy: number, r: number, startDeg: number, sweepDeg: number, steps: number): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = ((startDeg + (sweepDeg * i) / steps) * Math.PI) / 180;
    out.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return out;
}

const dist = (a: Pt, b: Pt) => Math.hypot(b[0] - a[0], b[1] - a[1]);

/** Point at `d` along the flattened path (all subpaths concatenated). */
export function pointAt(f: Flat, d: number): Pt | undefined {
  let rem = d;
  for (let s = 0; s < f.subpaths.length; s++) {
    const sp = f.subpaths[s]!;
    const c = f.cum[s]!;
    const total = c[c.length - 1] ?? 0;
    if (rem > total) { rem -= total; continue; }
    const pts = sp.closed && sp.pts.length > 1 ? [...sp.pts, sp.pts[0]!] : sp.pts;
    for (let i = 1; i < pts.length; i++) {
      if (rem <= c[i]!) {
        const seg = c[i]! - c[i - 1]!;
        const t = seg === 0 ? 0 : (rem - c[i - 1]!) / seg;
        const a = pts[i - 1]!, b = pts[i]!;
        return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
      }
    }
    return pts[pts.length - 1];
  }
  const last = f.subpaths[f.subpaths.length - 1];
  return last?.pts[last.pts.length - 1];
}

/** The leading `fraction` of the path, as open polylines. */
export function prefix(f: Flat, fraction: number): Subpath[] {
  let rem = f.length * Math.max(0, Math.min(1, fraction));
  const out: Subpath[] = [];
  for (let s = 0; s < f.subpaths.length && rem > 0; s++) {
    const sp = f.subpaths[s]!;
    const c = f.cum[s]!;
    const pts = sp.closed && sp.pts.length > 1 ? [...sp.pts, sp.pts[0]!] : sp.pts;
    const total = c[c.length - 1] ?? 0;
    if (rem >= total) { out.push({ pts, closed: false }); rem -= total; continue; }
    const acc: Pt[] = [pts[0]!];
    for (let i = 1; i < pts.length; i++) {
      if (rem >= c[i]!) { acc.push(pts[i]!); continue; }
      const seg = c[i]! - c[i - 1]!;
      const t = seg === 0 ? 0 : (rem - c[i - 1]!) / seg;
      const a = pts[i - 1]!, b = pts[i]!;
      acc.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
      break;
    }
    out.push({ pts: acc, closed: false });
    rem = 0;
  }
  return out;
}

// --- SVG path data: M L H V C Q Z (absolute + relative), no arcs -----------

export function parsePath(d: string): Subpath[] {
  const tokens = d.match(/[MmLlHhVvCcQqZz]|-?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g) ?? [];
  const out: Subpath[] = [];
  let cur: Pt = [0, 0], start: Pt = [0, 0], cmd = "", i = 0, sp: Subpath | undefined;
  const num = () => { const v = parseFloat(tokens[i++] ?? "0"); return v; };
  const pt = (rel: boolean): Pt => { const x = num(), y = num(); return rel ? [cur[0] + x, cur[1] + y] : [x, y]; };
  const ensure = () => { if (!sp) { sp = { pts: [cur], closed: false }; out.push(sp); } return sp; };
  while (i < tokens.length) {
    const t = tokens[i]!;
    if (/^[A-Za-z]$/.test(t)) { cmd = t; i++; } else if (cmd === "M") cmd = "L"; else if (cmd === "m") cmd = "l";
    switch (cmd) {
      case "M": case "m": cur = pt(cmd === "m"); start = cur; sp = { pts: [cur], closed: false }; out.push(sp); break;
      case "L": case "l": cur = pt(cmd === "l"); ensure().pts.push(cur); break;
      case "H": cur = [num(), cur[1]]; ensure().pts.push(cur); break;
      case "h": cur = [cur[0] + num(), cur[1]]; ensure().pts.push(cur); break;
      case "V": cur = [cur[0], num()]; ensure().pts.push(cur); break;
      case "v": cur = [cur[0], cur[1] + num()]; ensure().pts.push(cur); break;
      case "C": case "c": {
        const rel = cmd === "c"; const c1 = pt(rel), c2 = pt(rel), p = pt(rel);
        const s = ensure(); const p0 = cur;
        for (let k = 1; k <= CURVE_STEPS; k++) { const u = k / CURVE_STEPS; s.pts.push(cubic(p0, c1, c2, p, u)); }
        cur = p; break;
      }
      case "Q": case "q": {
        const rel = cmd === "q"; const c = pt(rel), p = pt(rel);
        const s = ensure(); const p0 = cur;
        for (let k = 1; k <= CURVE_STEPS; k++) { const u = k / CURVE_STEPS; s.pts.push(quad(p0, c, p, u)); }
        cur = p; break;
      }
      case "Z": case "z": if (sp) sp.closed = true; cur = start; sp = undefined; break;
      default: i++;
    }
  }
  return out;
}

const cubic = (p0: Pt, p1: Pt, p2: Pt, p3: Pt, t: number): Pt => {
  const m = 1 - t;
  return [
    m * m * m * p0[0] + 3 * m * m * t * p1[0] + 3 * m * t * t * p2[0] + t * t * t * p3[0],
    m * m * m * p0[1] + 3 * m * m * t * p1[1] + 3 * m * t * t * p2[1] + t * t * t * p3[1],
  ];
};
const quad = (p0: Pt, p1: Pt, p2: Pt, t: number): Pt => {
  const m = 1 - t;
  return [m * m * p0[0] + 2 * m * t * p1[0] + t * t * p2[0], m * m * p0[1] + 2 * m * t * p1[1] + t * t * p2[1]];
};
